<template>
  <el-select
    v-model="selectedValue"
    filterable
    remote
    :remote-method="remoteMethod"
    :loading="loading"
    @visible-change="handleVisibleChange"
    @change="handleChange"
    placeholder="请选择"
    class="pagination-select"
  >
    <el-option
      v-for="item in options"
      :key="getOptionKey(item)"
      :label="getOptionLabel(item)"
      :value="getOptionValue(item)"
      :disabled="item.disabled"
    >
      <slot name="option" :item="item">
        <!-- 默认选项模板 -->
        <div class="option-content">
          <div class="option-main">{{ getOptionLabel(item) }}</div>
          <div v-if="item.extraInfo" class="option-extra">
            {{ item.extraInfo }}
          </div>
        </div>
      </slot>
    </el-option>
    
    <!-- 加载更多选项 -->
    <div 
      v-if="hasMore" 
      class="load-more-option"
      @click="loadMore"
    >
      <div v-if="loadingMore" class="loading-text">
        <i class="el-icon-loading"></i>
        加载中...
      </div>
      <div v-else class="load-more-text">
        点击加载更多
      </div>
    </div>
    
    <!-- 无更多数据提示 -->
    <div 
      v-if="!hasMore && options.length > 0" 
      class="no-more-option"
    >
      没有更多数据了
    </div>
    
    <!-- 空数据提示 -->
    <div 
      v-if="!loading && options.length === 0 && pageParams.keyword" 
      class="empty-option"
    >
      未找到匹配的数据
    </div>
  </el-select>
</template>

<script>
export default {
  name: 'PaginationSelectWithExtra',
  props: {
    // 基本 props
    value: {
      type: [String, Number, Array],
      default: ''
    },
    // API 方法
    apiMethod: {
      type: Function,
      default: null
    },
    // 配置项
    config: {
      type: Object,
      default: () => ({
        pageSize: 20,
        valueField: 'value',      // 值字段名
        labelField: 'label',      // 显示字段名
        keyField: 'id',           // 唯一标识字段名
        extraFields: [],          // 需要额外携带的字段
        multiple: false           // 是否多选
      })
    }
  },
  data() {
    return {
      selectedValue: this.value,
      selectedItem: null,         // 当前选中的完整对象
      selectedItems: [],          // 多选时的选中项数组
      options: [],
      loading: false,
      loadingMore: false,
      
      // 分页参数
      pageParams: {
        page: 1,
        pageSize: this.config.pageSize || 20,
        keyword: '',
        total: 0
      },
      
      hasMore: true,
      searchTimer: null,
      
      // 缓存选中的完整对象
      valueToItemMap: new Map()
    }
  },
  computed: {
    // 获取配置项
    valueField() {
      return this.config.valueField || 'value'
    },
    labelField() {
      return this.config.labelField || 'label'
    },
    keyField() {
      return this.config.keyField || 'id'
    },
    extraFields() {
      return this.config.extraFields || []
    }
  },
  watch: {
    value(newVal) {
      this.selectedValue = newVal
      // 如果值改变，尝试查找对应的完整对象
      if (newVal) {
        if (this.config.multiple && Array.isArray(newVal)) {
          this.selectedItems = newVal.map(val => 
            this.valueToItemMap.get(val) || { [this.valueField]: val }
          )
        } else {
          this.selectedItem = this.valueToItemMap.get(newVal) || null
        }
      }
    },
    selectedValue(newVal) {
      this.$emit('input', newVal)
      this.$emit('change', newVal)
    },
    selectedItem(newItem) {
      this.$emit('select-change', newItem)
    },
    selectedItems(newItems) {
      this.$emit('select-change', newItems)
    }
  },
  mounted() {
    // 初始加载
    this.fetchOptions(true)
  },
  methods: {
    // 获取选项的唯一 key
    getOptionKey(item) {
      return item[this.keyField] || item[this.valueField]
    },
    
    // 获取选项显示文本
    getOptionLabel(item) {
      return item[this.labelField] || item[this.valueField] || ''
    },
    
    // 获取选项值
    getOptionValue(item) {
      return item[this.valueField]
    },
    
    // 搜索方法
    remoteMethod(query) {
      clearTimeout(this.searchTimer)
      
      this.searchTimer = setTimeout(() => {
        this.pageParams.keyword = query
        this.pageParams.page = 1
        this.fetchOptions(true)
      }, 300)
    },
    
    // 加载更多
    loadMore() {
      if (this.loadingMore || !this.hasMore) return
      
      this.loadingMore = true
      this.pageParams.page++
      this.fetchOptions(false)
    },
    
    // 获取选项数据
    async fetchOptions(isReset = false) {
      if (isReset) {
        this.options = []
        this.loading = true
      } else {
        this.loadingMore = true
      }
      
      try {
        let response
        
        // 使用自定义 API 方法或默认模拟方法
        if (typeof this.apiMethod === 'function') {
          response = await this.apiMethod(this.pageParams)
        } else {
          response = await this.mockApi(this.pageParams)
        }
        
        const { data, total } = response
        
        if (isReset) {
          this.options = data
        } else {
          // 避免重复数据
          const existingKeys = new Set(this.options.map(item => this.getOptionKey(item)))
          const newData = data.filter(item => 
            !existingKeys.has(this.getOptionKey(item))
          )
          this.options = [...this.options, ...newData]
        }
        
        // 更新缓存映射
        this.updateValueToItemMap(data)
        
        this.pageParams.total = total
        this.hasMore = this.options.length < total
        
        // 触发加载成功事件
        this.$emit('load-success', {
          data: this.options,
          page: this.pageParams.page,
          total: total
        })
        
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$emit('load-error', error)
        
        // 加载失败时回退页码
        if (!isReset && this.pageParams.page > 1) {
          this.pageParams.page--
        }
        
        this.$message.error('数据加载失败')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    
    // 更新值到对象的映射缓存
    updateValueToItemMap(data) {
      data.forEach(item => {
        const value = this.getOptionValue(item)
        if (value !== undefined && value !== null) {
          this.valueToItemMap.set(value, item)
        }
      })
    },
    
    // 选项变更处理
    handleChange(value) {
      if (this.config.multiple) {
        // 多选模式
        this.selectedItems = value.map(val => 
          this.valueToItemMap.get(val) || { [this.valueField]: val }
        )
        this.$emit('select-change', this.selectedItems)
      } else {
        // 单选模式
        this.selectedItem = this.valueToItemMap.get(value) || null
        this.$emit('select-change', this.selectedItem)
      }
      
      // 额外触发携带完整对象的事件
      if (this.config.multiple) {
        const extraData = this.selectedItems.map(item => this.extractExtraData(item))
        this.$emit('extra-change', extraData)
      } else {
        const extraData = this.extractExtraData(this.selectedItem)
        this.$emit('extra-change', extraData)
      }
    },
    
    // 提取额外携带的数据
    extractExtraData(item) {
      if (!item) return null
      
      const result = { [this.valueField]: item[this.valueField] }
      
      // 提取配置的额外字段
      this.extraFields.forEach(field => {
        if (item[field] !== undefined) {
          result[field] = item[field]
        }
      })
      
      // 总是携带标签字段
      result[this.labelField] = item[this.labelField]
      
      return result
    },
    
    // 获取当前选中的完整数据
    getSelectedData() {
      if (this.config.multiple) {
        return this.selectedItems.map(item => this.extractExtraData(item))
      }
      return this.extractExtraData(this.selectedItem)
    },
    
    // 根据值设置选中项
    setSelectedByValue(value, extraData = null) {
      this.selectedValue = value
      
      if (extraData) {
        if (this.config.multiple && Array.isArray(value) && Array.isArray(extraData)) {
          extraData.forEach((data, index) => {
            const val = value[index]
            if (val !== undefined && data) {
              this.valueToItemMap.set(val, {
                [this.valueField]: val,
                [this.labelField]: data[this.labelField],
                ...data
              })
            }
          })
        } else if (!this.config.multiple && extraData) {
          this.valueToItemMap.set(value, {
            [this.valueField]: value,
            [this.labelField]: extraData[this.labelField],
            ...extraData
          })
        }
      }
    },
    
    // 模拟 API
    mockApi(params) {
      return new Promise((resolve) => {
        setTimeout(() => {
          // 生成包含额外字段的模拟数据
          const allData = Array.from({ length: 100 }, (_, i) => ({
            id: `item_${i + 1}`,                    // 唯一标识
            value: `value_${i + 1}`,                // 实际值
            label: `选项 ${i + 1}`,                  // 显示文本
            code: `CODE_${String(i + 1).padStart(3, '0')}`, // 额外字段1
            type: i % 3 === 0 ? 'A类' : i % 3 === 1 ? 'B类' : 'C类', // 额外字段2
            status: i % 2 === 0 ? '启用' : '停用',   // 额外字段3
            price: (Math.random() * 1000).toFixed(2), // 额外字段4
            extraInfo: `这是第 ${i + 1} 个选项的额外信息`, // 额外字段5
            disabled: i % 10 === 0                  // 是否禁用
          }))
          
          // 如果有关键字，进行过滤
          let filteredData = allData
          if (params.keyword) {
            filteredData = allData.filter(item => 
              item.label.includes(params.keyword) || 
              item.code.includes(params.keyword) ||
              item.type.includes(params.keyword)
            )
          }
          
          // 分页处理
          const start = (params.page - 1) * params.pageSize
          const end = start + params.pageSize
          const data = filteredData.slice(start, end)
          
          resolve({
            data,
            total: filteredData.length
          })
        }, 500)
      })
    },
    
    // 重置
    reset() {
      this.selectedValue = this.config.multiple ? [] : ''
      this.selectedItem = null
      this.selectedItems = []
      this.pageParams.page = 1
      this.pageParams.keyword = ''
      this.hasMore = true
      this.valueToItemMap.clear()
      this.fetchOptions(true)
    },
    
    // 重新加载
    reload() {
      this.pageParams.page = 1
      this.fetchOptions(true)
    }
  }
}
</script>

<style scoped>
.pagination-select {
  width: 100%;
}

/* 选项内容样式 */
.option-content {
  display: flex;
  flex-direction: column;
  line-height: 1.4;
}

.option-main {
  font-weight: normal;
  color: #333;
}

.option-extra {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

/* 加载更多选项样式 */
.load-more-option,
.no-more-option,
.empty-option {
  height: 36px;
  line-height: 36px;
  padding: 0 20px;
  color: #909399;
  cursor: pointer;
  text-align: center;
  font-size: 14px;
  box-sizing: border-box;
}

.load-more-option:hover {
  background-color: #f5f7fa;
}

.loading-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-text .el-icon-loading {
  color: #409eff;
  font-size: 16px;
}

.load-more-text {
  color: #409eff;
  transition: color 0.3s;
}

.load-more-text:hover {
  color: #66b1ff;
}

.no-more-option {
  color: #c0c4cc;
  cursor: default;
}

.empty-option {
  color: #c0c4cc;
  cursor: default;
  font-style: italic;
}

/* 自定义选项样式 */
:deep(.el-select-dropdown__item) {
  padding: 8px 20px;
  height: auto;
  line-height: normal;
}

:deep(.el-select-dropdown__item.selected) .option-main {
  font-weight: bold;
  color: #409eff;
}

:deep(.el-select-dropdown__item.selected) .option-extra {
  color: #67c23a;
}
</style>